/**
 * Browser-safe Supabase Storage URL projection.
 *
 * Keep this module free of product image selection data. Client components use
 * it on every route, while the full canonical image resolver carries the
 * visually reviewed exclusion registry and belongs on the server/data path.
 */
export const PROXIED_STORAGE_BUCKETS = [
  'downloads',
  'brand-logos',
  'menu-images',
  'press-logos',
  'company-logos',
  'authors',
  'product-images',
  'blog-images',
  'state-flags',
] as const

const STORAGE_PUBLIC_MARKER = '/storage/v1/object/public/'

export function parseProxiedStorageUrl(url: string): { bucket: string; rest: string } | null {
  const idx = url.indexOf(STORAGE_PUBLIC_MARKER)
  if (idx === -1) return null
  const rest = url.slice(idx + STORAGE_PUBLIC_MARKER.length).replace(/^\/+/, '')
  const bucket = rest.split(/[/?#]/)[0]
  if (!(PROXIED_STORAGE_BUCKETS as readonly string[]).includes(bucket)) return null
  return { bucket, rest }
}

/** Return a same-origin path for public objects in a proxied storage bucket. */
export function getProxiedStoragePath<T extends string | null | undefined>(url: T): T | string {
  if (!url) return url
  const parsed = parseProxiedStorageUrl(url)
  return parsed ? `/${parsed.rest}` : url
}

/** True for an absolute web URL or a same-origin path in a proxied bucket. */
export function isRenderableImageUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0) return false
  if (/^https?:\/\//i.test(url)) return true
  const bucket = url.replace(/^\/+/, '').split(/[/?#]/)[0]
  return url.startsWith('/') && (PROXIED_STORAGE_BUCKETS as readonly string[]).includes(bucket)
}
